import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Flame, ChevronRight } from "lucide-react";
import { toast } from "sonner";

function localDayISO() {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function num(n) {
  if (n == null || Number.isNaN(n)) return 0;
  return Math.max(0, Math.round(Number(n)));
}

function deriveGoals(user) {
  const cal = user?.calorie_goal ? Number(user.calorie_goal) : null;
  const auto = (pct, kcalPerG) => (cal ? Math.round((cal * pct) / kcalPerG) : null);
  return {
    calories: cal,
    protein_g: user?.protein_goal_g ?? auto(0.3, 4),
    carbs_g: user?.carbs_goal_g ?? auto(0.45, 4),
    fat_g: user?.fat_goal_g ?? auto(0.25, 9),
    sugar_g: user?.sugar_goal_g ?? 50,
    fiber_g: user?.fiber_goal_g ?? 30,
    sodium_mg: user?.sodium_goal_mg ?? 2300,
  };
}

const MACROS = [
  { key: "protein_g", label: "Protein", unit: "g",  color: "#E11D48" },
  { key: "carbs_g",   label: "Carbs",   unit: "g",  color: "#3B82F6" },
  { key: "fat_g",     label: "Fat",     unit: "g",  color: "#F97316" },
  { key: "sugar_g",   label: "Sugar",   unit: "g",  color: "#EC4899", inverse: true },
  { key: "fiber_g",   label: "Fiber",   unit: "g",  color: "#8B5CF6" },
  { key: "sodium_mg", label: "Sodium",  unit: "mg", color: "#64748B", inverse: true },
];

/**
 * Compact Dashboard nutrition summary.
 * Big serif calorie hero on the left, 6 macro mini-cells on the right.
 * Whole strip is a Link to /nutrition.
 */
export default function NutritionSummaryStrip() {
  const { user } = useAuth();
  const [totals, setTotals] = useState({ calories: 0, protein_g: 0, carbs_g: 0, fat_g: 0, sugar_g: 0, fiber_g: 0, sodium_mg: 0, count: 0 });

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/nutrition/today", { params: { day: localDayISO() } });
      setTotals(data.totals || totals);
    } catch (err) {
      const status = err?.response?.status;
      if (status && status >= 500) toast.error(formatApiError(err));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { load(); }, [load]);

  const goals = deriveGoals(user);
  const calPct = goals.calories ? Math.min(100, (num(totals.calories) / Number(goals.calories)) * 100) : 0;

  return (
    <Link
      to="/nutrition"
      className="block group"
      data-testid="dashboard-nutrition-strip"
      aria-label="Open Nutrition log"
    >
      <div className="flex items-baseline justify-between mb-3">
        <div className="font-mono-num text-[11px] uppercase tracking-[0.3em] text-[#0D9488] flex items-center gap-2">
          // Fuel · Today
        </div>
        <div className="font-mono-num text-[10px] uppercase tracking-widest text-slate-500 group-hover:text-[#0D9488] transition-colors flex items-center gap-1">
          View log <ChevronRight className="w-3 h-3" />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {/* Hero — Calories, big serif */}
        <div
          className="relative overflow-hidden border border-slate-200 bg-slate-50 p-4 md:col-span-1 group-hover:border-slate-300 transition-colors"
          data-testid="dashboard-cal-tile"
        >
          {goals.calories != null && (
            <div
              aria-hidden
              className="absolute inset-y-0 left-0 pointer-events-none bg-emerald-500/10 transition-all duration-700 ease-out"
              style={{ width: `${calPct}%` }}
            />
          )}
          <div className="relative">
            <div className="flex items-center gap-1.5">
              <span className="inline-block w-2 h-2 rounded-full bg-emerald-500" />
              <Flame className="w-3.5 h-3.5 text-[#0D9488]" />
              <span className="font-mono-num text-[10px] uppercase tracking-widest text-slate-600">Calories</span>
            </div>
            <div className="font-serif-stat text-3xl md:text-4xl leading-none mt-2 text-slate-900 dark:text-white tabular-nums">
              {num(totals.calories).toLocaleString()}
              {goals.calories != null && (
                <span className="text-slate-400 text-base font-normal ml-1 tabular-nums">
                  / {Number(goals.calories).toLocaleString()}
                </span>
              )}
            </div>
            <div className="font-mono-num text-[10px] text-slate-500 uppercase tracking-widest mt-2">
              kcal today {goals.calories != null ? `· ${Math.round(calPct)}%` : "· no goal"}
            </div>
          </div>
        </div>

        {/* 6-macro grid */}
        <div className="md:col-span-2 grid grid-cols-3 gap-2">
          {MACROS.map((m) => {
            const val = num(totals[m.key]);
            const goal = goals[m.key] != null ? Number(goals[m.key]) : null;
            const pct = goal ? Math.min(100, (val / goal) * 100) : 0;
            const reached = goal && val >= goal;
            const barColor = m.inverse && reached ? "#EF4444" : m.color;
            return (
              <div
                key={m.key}
                className="border border-slate-200 bg-slate-50 px-2.5 py-2 group-hover:border-slate-300 transition-colors"
                data-testid={`dashboard-macro-${m.key}`}
              >
                <div className="flex items-center gap-1 mb-1">
                  <span className="inline-block w-1.5 h-1.5 rounded-full" style={{ background: m.color }} aria-hidden />
                  <span className="font-mono-num text-[9px] uppercase tracking-widest text-slate-500">{m.label}</span>
                </div>
                <div className="font-display font-bold text-lg leading-none text-slate-900 dark:text-white tabular-nums">
                  {val.toLocaleString()}
                  {goal != null && (
                    <span className="text-slate-400 text-[10px] font-normal ml-0.5 tabular-nums">
                      /{goal.toLocaleString()}
                    </span>
                  )}
                </div>
                <div className="h-0.5 w-full bg-slate-50 relative overflow-hidden mt-1.5">
                  {goal != null && (
                    <div
                      className="absolute inset-y-0 left-0 transition-all duration-700 ease-out"
                      style={{ width: `${pct}%`, background: barColor }}
                      aria-hidden
                    />
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </Link>
  );
}
